import React, { useContext, useEffect, useState, useCallback } from "react";
import { Card, Avatar, Button, Dropdown, Spin, App } from "antd";
import {
  UserOutlined,
  VideoCameraOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { apiGetDashboard } from "../api/dashboard";
import { useNavigate } from "react-router-dom";
import { formatTime, getTimeUntilAppointment } from "../common/utils";
import { AppContext } from "../AppContext";

const Dashboard = () => {
  const { message } = App.useApp();
  const { state } = useContext(AppContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("today");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [notificationsCount, setNotificationsCount] = useState(0);

  const { displayName } = state?.currentUser;

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const filterMap = {
        today: 1,
        week: 2,
        month: 3,
      };

      const filter = filterMap[activeTab];
      const res = await apiGetDashboard(filter);

      setDashboardData(res?.data);
      if (res?.data?.notificationsCount !== undefined) {
        setNotificationsCount(res?.data?.notificationsCount);
      }
    } catch (error) {
      message.error("Không thể tải dữ liệu bảng điều khiển");
    } finally {
      setLoading(false);
    }
  }, [activeTab, message]);

  const formatTime = (timestampInSeconds) => {
    if (!timestampInSeconds) return "";

    return new Date(timestampInSeconds * 1000).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const handleNotificationReceived = () => {
      setNotificationsCount((prevCount) => {
        const newCount = prevCount + 1;
        return newCount;
      });
    };

    const handleNotificationClicked = () => {
      // Notification clicked - count will sync when dashboard data is refreshed
      // No action needed here for real-time updates
    };

    window.addEventListener('notification-received', handleNotificationReceived);
    window.addEventListener('notification-clicked', handleNotificationClicked);

    if ('serviceWorker' in navigator) {
      const handleServiceWorkerMessage = (event) => {
        if (event.data) {
          if (event.data.type === 'NOTIFICATION_RECEIVED') {
            setNotificationsCount((prevCount) => {
              const newCount = prevCount + 1;
              return newCount;
            });
          } else if (event.data.type === 'NOTIFICATION_CLICK') {
            // Notification clicked - count will sync when dashboard data is refreshed
            // No action needed here for real-time updates
          }
        }
      };
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
      
      return () => {
        window.removeEventListener('notification-received', handleNotificationReceived);
        window.removeEventListener('notification-clicked', handleNotificationClicked);
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      };
    }

    return () => {
      window.removeEventListener('notification-received', handleNotificationReceived);
      window.removeEventListener('notification-clicked', handleNotificationClicked);
    };
  }, []);

  // Handle menu click
  const handleMenuClick = (key, appointment) => {
    switch (key) {
      case "view":
        navigate(`/appointments/${appointment.id}`);
        break;
      case "reschedule":
        navigate(`/appointments/reschedule/${appointment.id}`);
        break;
      case "video":
        // Handle video call
        if (appointment.videoCallUrl) {
          window.open(appointment.videoCallUrl, "_blank");
        } else {
          message.info("Cuộc gọi video sẽ sớm bắt đầu");
        }
        break;
      default:
        break;
    }
  };

  const actionMenu = (appointment) => ({
    items: [
      {
        key: "view",
        label: "Xem chi tiết",
      },
      {
        key: "reschedule",
        label: "Lên lịch lại",
        disabled: !appointment.reschedule,
      },
    ],
    onClick: ({ key }) => handleMenuClick(key, appointment),
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Không có dữ liệu nào khả dụng
            </h2>
            <Button type="primary" onClick={fetchDashboardData}>
              Thử lại
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Map API data to stats cards
  const statsData = [
    {
      key: "patients",
      title: "Bệnh nhân",
      count: dashboardData?.patients?.total || 0,
      subtitle:
        dashboardData?.patients?.new > 0
          ? `+${dashboardData?.patients?.new} bệnh nhân mới`
          : "Không có bệnh nhân mới",
      icon: (
        <img
          src="/icon/dashboard_patients_logo.png"
          alt="MedRev Logo"
          className="w-10 h-10"
        />
      ),
      bgColor: "bg-purple-50",
    },
    {
      key: "appointments",
      title: "Lịch hẹn hôm nay",
      count: dashboardData?.todayAppointments?.count || 0,
      subtitle: dashboardData?.todayAppointments?.nextAppointmentAt
        ? `Cuộc hẹn tiếp theo bắt đầu lúc ${formatTime(
            dashboardData.todayAppointments.nextAppointmentAt
          )}`
        : "Không có cuộc hẹn nào sắp tới",
      icon: (
        <img
          src="/icon/dashboard_appointments_logo.png"
          alt="MedRev Logo"
          className="w-10 h-10"
        />
      ),
      bgColor: "bg-purple-50",
    },
    {
      key: "notifications",
      title: "Thông báo",
      count: notificationsCount,
      subtitle: "Tin nhắn chưa đọc",
      icon: (
        <img
          src="/icon/dashboard_notification_logo.png"
          alt="MedRev Logo"
          className="w-10 h-10"
        />
      ),
      bgColor: "bg-purple-50",
    },
    {
      key: "prescriptions",
      title: "Đơn thuốc đang chờ xử lý",
      count: dashboardData?.pendingPrescriptions?.pending || 0,
      subtitle:
        dashboardData?.pendingPrescriptions?.urgentReviews > 0
          ? `${dashboardData?.pendingPrescriptions?.urgentReviews} đánh giá khẩn cấp`
          : "Không có đánh giá khẩn cấp",
      icon: (
        <img
          src="/icon/dashboard_prescription_logo.png"
          alt="MedRev Logo"
          className="w-10 h-10"
        />
      ),
      bgColor: "bg-red-50",
      urgent: dashboardData?.pendingPrescriptions?.urgentReviews > 0,
    },
  ];

  const redirectTo = (key) => {
    switch (key) {
      case "patients":
        navigate("/patients");
        break;
      case "appointments":
        navigate("/appointments");
        break;
      case "notifications":
        navigate("/notifications");
        break;
      case "prescriptions":
        navigate("/follow-ups");
        break;
      default:
        break;
    }
  };

  const appointments = dashboardData?.upcomingAppointments || [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-800">
          Chào mừng, {displayName || "Doctor"}
        </h1>
        <p className="text-gray-500 mt-1">
          Dưới đây là tóm tắt nhanh về một ngày làm việc và những hoạt động chính của bạn.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsData?.map((stat, index) => (
          <Card
            key={index}
            hoverable
            className="shadow-sm hover:shadow-md transition-shadow"
            onClick={() => redirectTo(stat.key)}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-500 text-sm">{stat?.title}</p>
              <div className={`p-2 rounded-lg`}>{stat?.icon}</div>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-1">
              {stat?.count}
            </h2>
            <p
              className={`text-sm ${
                stat?.urgent ? "text-red-500" : "text-gray-400"
              }`}
            >
              {stat?.urgent && "🔴 "}
              {stat?.subtitle}
            </p>
          </Card>
        ))}
      </div>

      {/* Upcoming Appointments */}
      <Card className="shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            Các cuộc hẹn sắp tới
          </h2>
          <div className="flex gap-2">
            <Button
              variant="outlined"
              color={activeTab === "today" && "primary"}
              size="small"
              onClick={() => setActiveTab("today")}
            >
              Hôm nay
            </Button>
            <Button
              variant="outlined"
              color={activeTab === "week" && "primary"}
              size="small"
              onClick={() => setActiveTab("week")}
            >
              Tuần
            </Button>
            <Button
              variant="outlined"
              color={activeTab === "month" && "primary"}
              size="small"
              onClick={() => setActiveTab("month")}
            >
              Tháng
            </Button>
          </div>
        </div>

        {/* Appointments List */}
        {appointments?.appointments?.length > 0 ? (
          <div className="space-y-4">
            {appointments?.appointments?.map((appointment, index) => (
              <div
                key={appointment.id}
                className="relative flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg transition-all group hover:bg-purple-50"
              >
                <div className="flex items-center gap-4 flex-1">
                  {index === 0 && (
                    <div className="absolute left-0 w-1 h-16 bg-purple-600 rounded-r"></div>
                  )}

                  <Avatar
                    size={48}
                    icon={appointment.patientImage ?? <UserOutlined />}
                    src={appointment.patientImage}
                  >
                    {!appointment?.patientImage &&
                      appointment.patientName?.charAt(0)?.toUpperCase()}
                  </Avatar>

                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">
                      {appointment.patientName}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {appointment.subject}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-1">
                  <span className="text-gray-700 font-medium">
                    {formatTime(
                      appointment.startTimestamp,
                      appointment.endTimestamp
                    )}
                    {getTimeUntilAppointment(appointment.startTimestamp) && (
                      <p className="text-xs text-purple-600 mt-1">
                        {getTimeUntilAppointment(appointment.startTimestamp)}
                      </p>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <Button
                    type="primary"
                    icon={<VideoCameraOutlined />}
                    disabled={!appointment?.joinVideoCall}
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={() => navigate(`/video-call/${appointment.id}`)}
                  />
                  <Dropdown
                    menu={actionMenu(appointment)}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <Button
                      icon={<MoreOutlined />}
                      type="text"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            Chưa có lịch hẹn nào được lên kế hoạch
          </div>
        )}

        {appointments?.viewAll && (
          <div className="text-center mt-6">
            <Button
              variant="link"
              color="primary"
              className="text-purple-600"
              onClick={() => navigate("/appointments")}
            >
              Xem tất cả các cuộc hẹn
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
